import { GlobalConfigType } from 'actions'
import { FieldOrigin, InputType, LoanFieldProps, loanOptionsConstants } from 'config'
import { store } from 'reducers'
import type { FormTableHeader } from 'stories/components'

export const header: FormTableHeader[] = [
  {
    key: 'propertyTypeAddress',
    title: (
      <span>
        <div className="border-b w-fit mb-1 border-gray-300">Property Type</div>
        Property Address
      </span>
    ),
  },
  {
    key: 'purchasePriceDate',
    title: (
      <span>
        <div className="border-b w-fit mb-1 border-gray-300">Purchase Price</div>
        Purchase Date
      </span>
    ),
  },
  {
    key: 'renovationBudget',
    title: (
      <span>
        Renovation
        <br />
        Budget
      </span>
    ),
  },
  {
    key: 'asRental',
    title: (
      <span>
        Retained <br />
        As Rental?
      </span>
    ),
  },
  {
    key: 'salesPriceDate',
    title: (
      <span>
        <div className="border-b w-fit mb-1 border-gray-300">Sold Price</div>
        Sold Date
      </span>
    ),
  },
  {
    key: 'status',
    title: 'Status',
  },
]

export const hasRecordInputs = (): Record<string, InputType> => {
  const { auth: { role: { role: { permissions = [] } = {} } = {} } = {} } = store.getState()

  const inputs: Record<string, InputType> = {
    hasTrackRecord: {
      inputType: 'check',
      title: 'Has Track Record?',
      required: true,
      history: true,
    },
  }

  if (permissions.includes('CAN_OVERRIDE_TRACK_RECORD'))
    inputs.isTrackRecordOverride = {
      inputType: 'check',
      title: 'Override Track Record',
      required: false,
      history: true,
    }

  return inputs
}

export const defaultInputs = (): Record<string, InputType> => {
  const { globalConfigReducer } = store.getState()
  const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps
  const { [FieldOrigin.TrackRecord]: inputProps = {} } = loanFieldProperties

  const {
    purchasePrice: { defaultValue: purchasePriceDefaultValue = '' } = {},
    purchaseDate: { defaultValue: purchaseDateDefaultValue = '' } = {},
    renovationBudget: { defaultValue: renovationBudgetDefaultValue = '' } = {},
  } = inputProps

  const inputs: Record<string, InputType> = {
    propertyAddress: {
      inputType: 'map',
      title: 'Property Address',
      sitexPro: true,
      required: true,
    },
    propertyType: {
      inputType: 'select',
      title: 'Property Type',
      options: loanOptionsConstants.propertyType,
      hasDefaultOption: true,
      required: true,
    },
    purchaseDate: {
      inputType: 'text',
      type: 'date',
      title: 'Purchase Date',
      required: true,
      value: purchaseDateDefaultValue,
    },
    purchasePrice: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Purchase Price',
      required: true,
      prefix: '$',
      value: purchasePriceDefaultValue,
    },
    renovationBudget: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Renovation Budget',
      required: true,
      prefix: '$',
      value: renovationBudgetDefaultValue,
    },
    asRental: {
      inputType: 'Check',
      title: 'Retained As Rental?',
      required: true,
    },
    salesDate: {
      inputType: 'text',
      type: 'date',
      title: 'Sold Date',
      isPastDate: true,
      required: true,
    },
    salesPrice: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Sold Price',
      required: true,
      prefix: '$',
    },
    titleHeldName: {
      inputType: 'text',
      title: 'Title Held in Name',
      isPastDate: true,
      required: true,
    },
    ownershipPercent: {
      inputType: 'text',
      type: 'number',
      title: '% of Ownership',
      required: true,
      prefix: '%',
    },
    additionalNotes: {
      span: 2,
      inputType: 'textarea',
      title: 'Additional Notes',
      rows: 2,
      required: false,
    },
    override: {
      inputType: 'toggle',
      title: 'Override Validation',
    },
  }

  Object.keys(inputProps)
    .filter((key) => key.startsWith('_'))
    .forEach((key) => {
      const orgKey = key.substring(1)
      if (inputProps[key] && inputProps[key].required !== undefined) inputs[orgKey].required = inputProps[key].required
    })

  return inputs
}
